import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ClipboardCheck, Mail, Phone, Smartphone, MapPin, FileText, ShieldCheck, AlertCircle, IdCard } from 'lucide-react';
import { ChangeEmailCard } from '../../components/admin/ClientAccountCards';
import { adminUserService, AdminUserListItem } from '../../services/adminUserService';
import { ETICHETTA_CONTATTO } from '../../utils/contatto';

function fmtDateTime(iso?: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function Campo({ label, icon: Icon, mono, wide, children }: {
  label: string; icon?: React.ComponentType<{ className?: string }>; mono?: boolean; wide?: boolean; children: React.ReactNode;
}) {
  return (
    <div className={wide ? 'sm:col-span-2' : undefined}>
      <span className="text-[11px] text-muted-foreground uppercase tracking-wide flex items-center gap-1">
        {Icon && <Icon className="w-3 h-3" />} {label}
      </span>
      <p className={`mt-0.5 ${mono ? 'font-mono' : ''}`}>{children || '—'}</p>
    </div>
  );
}

function stato(r: AdminUserListItem): { label: string; className: string } {
  if (r.attivo) return { label: 'Approvata', className: 'bg-emerald-100 text-emerald-700' };
  if (r.rejected) return { label: 'Respinta', className: 'bg-red-100 text-red-700' };
  return { label: 'In attesa', className: 'bg-amber-100 text-amber-700' };
}

/**
 * Scheda di una Registrazione (route `/admin/registrazioni/:userId`, da
 * Admin → Registrazioni → "Scheda Utente"): i dati lasciati sul sito, prima
 * che esista un Cliente. Un account approvato collegato a un Cliente porta
 * alla sua scheda; uno approvato senza Cliente è un Contatto
 * (`.scratch/contatti-lead/spec.md`): il Cliente nasce al primo ordine.
 */
export default function AdminRegistrationDetailPage() {
  const { userId = '' } = useParams<{ userId: string }>();
  const [d, setD] = useState<AdminUserListItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    adminUserService.get(userId)
      .then(setD)
      .catch((err: any) => setError(err?.response?.status === 404 ? 'Registrazione non trovata.' : 'Errore di caricamento.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [userId]);

  if (loading) {
    return (
      <div className="p-6" data-testid="registration-detail-loading">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-secondary rounded w-64" />
          <div className="h-32 bg-secondary rounded" />
        </div>
      </div>
    );
  }

  if (error || !d) {
    return (
      <div className="p-6" data-testid="registration-detail-error">
        <Link to="/admin/registrazioni" className="text-sm text-muted-foreground hover:underline mb-4 flex items-center gap-1 w-fit">
          <ArrowLeft className="w-4 h-4" /> Indietro
        </Link>
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-center">
          <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <p className="text-sm text-red-700">{error || 'Errore sconosciuto'}</p>
        </div>
      </div>
    );
  }

  const badge = stato(d);
  return (
    <div className="p-6 space-y-5" data-testid="registration-detail-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Link to="/admin/registrazioni" className="text-xs text-muted-foreground hover:underline flex items-center gap-1 mb-1 w-fit">
            <ArrowLeft className="w-3.5 h-3.5" /> Registrazioni
          </Link>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2" data-testid="registration-detail-company">
            <ClipboardCheck className="w-6 h-6 text-[hsl(214,58%,41%)]" />
            {d.ragioneSociale || d.email}
          </h1>
          <div className="flex items-center gap-2 mt-1.5 flex-wrap">
            <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${badge.className}`} data-testid="registration-detail-status">
              {badge.label}
            </span>
            {d.attivo && d.contatto && (
              <span
                className="px-2 py-0.5 rounded text-[10px] font-semibold bg-sky-100 text-sky-700"
                title="Account attivo senza cliente sul gestionale: il cliente viene creato al primo ordine."
                data-testid="registration-detail-contatto"
              >
                {ETICHETTA_CONTATTO}
              </span>
            )}
            {d.attivo && !d.contatto && d.idCliente && (
              <Link
                to={`/admin/clienti/${d.idCliente}`}
                className="inline-flex items-center gap-1 text-xs text-[hsl(214,58%,41%)] hover:underline font-medium"
                data-testid="registration-detail-cliente-link"
              >
                <IdCard className="w-3.5 h-3.5" /> Scheda Cliente {d.idCliente} →
              </Link>
            )}
          </div>
        </div>
      </div>

      <section className="rounded-xl border bg-white p-5" data-testid="registration-detail-info">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-4 pb-3 border-b">
          <FileText className="w-4 h-4" /> Dati della registrazione
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <Campo label="Referente">{`${d.nome} ${d.cognome}`.trim()}</Campo>
          <Campo label="Registrata il"><span data-testid="registration-detail-created">{fmtDateTime(d.createdAt)}</span></Campo>
          <Campo label="Partita IVA" mono>{d.partitaIva}</Campo>
          <Campo label="Codice Fiscale" mono>{d.codiceFiscale}</Campo>
          <Campo label="Email" icon={Mail}>{d.email}</Campo>
          <Campo label="PEC" icon={Mail}>{d.pec}</Campo>
          <Campo label="Telefono" icon={Phone}>{d.telefono}</Campo>
          <Campo label="Cellulare" icon={Smartphone}><span data-testid="registration-detail-cellulare">{d.cellulare || '—'}</span></Campo>
          <Campo label="Codice SDI" mono>{d.codiceSdi}</Campo>
          <Campo label="Parco macchine stimato">{d.parcoMacchineStimato}</Campo>
          <Campo label="Sede legale" icon={MapPin} wide>{[d.indirizzo, d.cap, d.citta, d.provincia].filter(Boolean).join(', ')}</Campo>
          {d.sedeOperativa && (
            <Campo label="Sede operativa" icon={MapPin} wide>
              {[d.sedeOperativa.indirizzo, d.sedeOperativa.cap, d.sedeOperativa.citta, d.sedeOperativa.provincia].filter(Boolean).join(', ')}
            </Campo>
          )}
          <Campo label="Marche di interesse" wide>{d.marcheInteresse.join(', ')}</Campo>
        </div>
      </section>

      <section className="rounded-xl border bg-white p-5" data-testid="section-account-web">
        <h2 className="text-sm font-semibold flex items-center gap-2 mb-4 pb-3 border-b">
          <ShieldCheck className="w-4 h-4" /> Account Web
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <ChangeEmailCard clientKey={userId} currentEmail={d.email} onSuccess={load} />
        </div>
      </section>
    </div>
  );
}
